import Button from "@/common/elements/Button";
import FormItem from "@/common/elements/InputElements/FormItem.jsx";
import Select from "@/common/elements/InputElements/Select";
import TextField from "@/common/elements/InputElements/TextField";
import Table from "@/common/elements/Table";
import { StyledFlex, StyledGridCol, StyledGridRow } from "@/common/styles";
import {
  InmateDetailsTableContainer,
  TableHeader,
} from "@/components/InmateDetails/styles";
import emptyImage from "@/assets/emptyImage.svg";
import Modal from "@/common/elements/Modal";
import { forwardRef, useEffect, useImperativeHandle, useState } from "react";
import { Form, Image, Upload } from "antd";
import { StyledUploadFile } from "../styles";
import FileUploader from "@/components/FileUploader";
import {
  filteredUndefinedData,
  getFinalPath,
  getOptionsFromRefList,
  hasCheckSecurity,
  isEmptyObject,
  isNonEmptyVariable,
} from "@/util/common_function";
import API_ENDPOINTS, { PHOTOS_BASE_URL } from "@/util/api";
import api from "@/services/axios_custom";
import { v4 as uuidV4 } from "uuid";
import { useDispatch, useSelector } from "react-redux";
import useDialog from "@/hooks/useDialog.jsx";
import { setBookings, setDeleteAPis } from "@/slices/bookingsSlice";
import { add } from "lodash";
import { Popconfirm } from "antd";
import { Entity_types, Security_Types } from "@/util/permission_bits";
import { LABELS_CAPITAL } from "@/util/usePermissionMask";
import {
  MODULES_LIST_WITH_ID,
  SUB_MODULES_LIST_WITH_ID,
} from "@/util/common_variable";

const DistinguishingCharacteristicsForm = forwardRef(
  (
    {
      formData,
      isQuickAdd = false,
      quickSave,
      bookingsFieldsEnabled,
      isEdit = false,
    },
    ref
  ) => {
    const dispatch = useDispatch();
    const [form] = Form.useForm();
    const [photoForm] = Form.useForm();
    const [imgModal, setImgModal] = useState(false);
    const [isImgUploading, setisImgUploading] = useState(false);
    const [uploadedImage, setUploadedImage] = useState(null);
    const [imgData, setImgData] = useState([]);
    const [selectedImg, setSelectedImg] = useState(null);
    const [isImgEdit, setIsImgEdit] = useState(false);
    const fullSearch = useSelector(state => state.fullSearch);

    useEffect(() => {
      if (isNonEmptyVariable(formData)) {
        form.setFieldsValue(formData);
        if (
          isNonEmptyVariable(formData?.distinguishingCharacteristicsPhotos) &&
          formData?.distinguishingCharacteristicsPhotos?.length
        ) {
          let di = formData?.distinguishingCharacteristicsPhotos;
          di = di?.map((d) => {
            return {
              ...d?.photos?.[0],
              uuid: uuidV4(),
            };
          });
          setImgData(di);
        } else {
          setImgData([]);
        }
      } else {
        form.resetFields();
      }

      if (!isEdit) {
        setImgData([]);
        setSelectedImg(null);
      }
    }, [formData, isEdit]);

    const handleImgModal = () => {
      photoForm.resetFields();
      setImgModal(!imgModal);
    };

    const handleFileUpload = (file) => {
      if (file) {
        setUploadedImage(file);
      } else {
        console.error("No file selected or invalid file format");
      }
    };

    const onSaveClick = () => {
      if (uploadedImage) {
        const formData = new FormData();
        formData.append("file", uploadedImage);
        formData.append("description", photoForm.getFieldValue("description"));
        formData.append("imageType", "Mug-Front");

        const config = {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        };

        setisImgUploading(true);
        api
          .post(API_ENDPOINTS.imageUpload, formData, config)
          .then((response) => {
            const { body } = response.data;
            setImgData((prev) => [...prev, body]);
            handleImgModal();
          })
          .catch((error) => {
            console.error("Image upload failed:", error);
          })
          .finally(() => {
            setisImgUploading(false);
            photoForm.resetFields();
          });
      }
    };

    const selectImgFn = (values) => {
      setSelectedImg(values);
    };

    const handleDelete = () => {
      let di = imgData;
      di = di?.filter((d) => d?.uuid !== selectedImg?.uuid);
      setImgData(di);

      if (isNonEmptyVariable(selectedImg?.photoId)) {
        const url = API_ENDPOINTS.bioPhotos + "/id/" + selectedImg?.photoId;
        dispatch(setDeleteAPis(url));
      }
    };

    useImperativeHandle(ref, () => ({
      validateFields: () => form.validateFields(),
      resetForm: () => form.resetFields(),
      getImgData: () => imgData,
      setImgDataEmpty: () => setImgData([]),
      setSelectedImgEmpty: () => setSelectedImg(null),
    }));

    return (
      <>
        <Form
          name="DistinguishingCharacteristics"
          form={form}
          disabled={!bookingsFieldsEnabled}
        >
          <StyledGridRow gutter={16}>
            <StyledGridCol span={8}>
              <FormItem
                name="characteristicType"
                label="Characteristic Type"
                required={!fullSearch}
                rules={
                  !fullSearch
                  ? [
                    {
                      required: true,
                      message: "Characteristic Type is required!",
                    },
                  ] : []
                }
              >
                <Select
                  options={getOptionsFromRefList(
                    "DistinguishingCharacteristics"
                  )}
                  placeholder="Primary Language"
                  showSearch={true}
                  variant="outlined"
                  disabled={!bookingsFieldsEnabled}
                />
              </FormItem>
            </StyledGridCol>

            <StyledGridCol span={8}>
              <FormItem
                name="location"
                label="Location"
                required={!fullSearch}
                rules={
                  !fullSearch
                  ? [
                    {
                      required: true,
                      message: "Location is required!",
                    },
                  ] : []
                }
              >
                <Select
                  options={getOptionsFromRefList("CharacteristicLocation")}
                  placeholder="Primary Language"
                  showSearch={true}
                  variant="outlined"
                  disabled={!bookingsFieldsEnabled}
                />
              </FormItem>
            </StyledGridCol>

            <StyledGridCol span={8}>
              <FormItem name="nist" label="NIST">
                <TextField />
              </FormItem>
            </StyledGridCol>
            <StyledGridCol flex={"auto"}>
              <FormItem name="description" label="Description">
                <TextField inputType="textarea" />
              </FormItem>
            </StyledGridCol>

            {/* isQuickAdd */}
            {isQuickAdd && (
              <StyledGridCol style={{ paddingTop: "30px" }}>
                <Button
                  disabled={
                    !bookingsFieldsEnabled ||
                    !hasCheckSecurity(
                      Security_Types.functionality,
                      Entity_types.subModule,
                      LABELS_CAPITAL.ADD,
                      MODULES_LIST_WITH_ID.MODULE_BOOKINGS,
                      SUB_MODULES_LIST_WITH_ID.Arrest_Report
                    )
                  }
                  onClick={quickSave}
                >
                  Q-Add
                </Button>
              </StyledGridCol>
            )}
          </StyledGridRow>
        </Form>

        {/* Photos */}

        {!isQuickAdd && (
          <>
            <InmateDetailsTableContainer>
              <TableHeader>
                <h6>List of photos</h6>

                <StyledFlex
                  justify="end"
                  gap="10px"
                  style={{ marginBottom: "10px" }}
                >
                  <Button
                    disabled={
                      !bookingsFieldsEnabled ||
                      !hasCheckSecurity(
                        Security_Types.functionality,
                        Entity_types.subModule,
                        LABELS_CAPITAL.ADD,
                        MODULES_LIST_WITH_ID.MODULE_BOOKINGS,
                        SUB_MODULES_LIST_WITH_ID.Arrest_Report
                      )
                    }
                    onClick={handleImgModal}
                  >
                    Add Photo
                  </Button>

                  <Popconfirm
                    title="Delete the photos"
                    description="Are you sure to delete this photo?"
                    onConfirm={handleDelete}
                    onCancel={() => {}}
                    okText="Yes"
                    cancelText="No"
                  >
                    <Button
                      disabled={
                        !bookingsFieldsEnabled ||
                        !hasCheckSecurity(
                          Security_Types.functionality,
                          Entity_types.subModule,
                          LABELS_CAPITAL.DELETE,
                          MODULES_LIST_WITH_ID.MODULE_BOOKINGS,
                          SUB_MODULES_LIST_WITH_ID.Arrest_Report
                        )
                      }
                    >
                      Delete
                    </Button>
                  </Popconfirm>
                </StyledFlex>
              </TableHeader>

              <StyledGridRow gutter={10}>
                <StyledGridCol span={16}>
                  {hasCheckSecurity(
                    Security_Types.functionality,
                    Entity_types.subModule,
                    LABELS_CAPITAL.VIEW,
                    MODULES_LIST_WITH_ID.MODULE_BOOKINGS,
                    SUB_MODULES_LIST_WITH_ID.Arrest_Report
                  ) && (
                    <Table
                      headers={[
                        { key: "description", text: "Description" },
                        { key: "imageType", text: "Image Type" },
                      ]}
                      data={imgData}
                      onRowClick={selectImgFn}
                    />
                  )}
                </StyledGridCol>

                <StyledGridCol span={8}>
                  <div>
                    <img
                      src={
                        isNonEmptyVariable(selectedImg)
                          ? PHOTOS_BASE_URL +
                            getFinalPath(selectedImg?.fullSizePath)
                          : emptyImage
                      }
                      width="100%"
                      style={{ border: "1px solid rgba(0,0,0,0.1" }}
                    />
                  </div>
                </StyledGridCol>
              </StyledGridRow>
            </InmateDetailsTableContainer>
            <Modal
              size=""
              header="Photo Information"
              style={{ minWidthL: "580px" }}
              show={imgModal}
              onHide={handleImgModal}
              footerActions={[
                {
                  key: "save",
                  text: isImgEdit ? "Update" : "Save",
                  onClick: onSaveClick,
                  disabled: isImgUploading,
                  isLoading: isImgUploading,
                },
              ]}
              footerCloseAction
              footerCloseActionText="Cancel"
            >
              <Form form={photoForm}>
                <StyledGridRow>
                  <StyledGridCol span={24}>
                    <FormItem name="description" label="Description">
                      <TextField inputType="textarea" />
                    </FormItem>
                  </StyledGridCol>
                  <StyledGridCol span={24}>
                    {/* Ant Upload */}
                    {/* <FileUploader canvasMaxHeight="300px" /> */}
                    <FileUploader
                      browseFileType="image"
                      acceptImageFormats="image/png,image/jpeg,image/jpg"
                      // canvasMaxHeight="500px"
                      allowMultiple={false}
                      onUpload={handleFileUpload}
                      imageUrlFromParent={
                        PHOTOS_BASE_URL +
                        getFinalPath(selectedImg?.photos?.[0]?.fullSizePath)
                      }
                    />
                  </StyledGridCol>
                </StyledGridRow>
              </Form>
            </Modal>
          </>
        )}
      </>
    );
  }
);

export default DistinguishingCharacteristicsForm;
